import { countOwnedTilesOfKind } from "./buildings";
import { getTile } from "./grid";
import type { GameState, PlayerId, Tile, Unit, VictoryCondition } from "./types";

export function activeVictoryCondition(
  source: Pick<GameState, "victoryConditions">,
): VictoryCondition {
  return source.victoryConditions?.[0] ?? "rout";
}

export function countOwnedCities(tiles: readonly Tile[], owner: PlayerId): number {
  return countOwnedTilesOfKind({ tiles, kind: "city", owner });
}

export function countOwnedMonuments(tiles: readonly Tile[], owner: PlayerId): number {
  return countOwnedTilesOfKind({ tiles, kind: "monument", owner });
}

function countMonuments(tiles: readonly Tile[]): number {
  return tiles.filter((tile) => tile.kind === "monument").length;
}

export function ownsAllMonuments({
  tiles,
  owner,
}: {
  tiles: readonly Tile[];
  owner: PlayerId;
}): boolean {
  const total = countMonuments(tiles);
  if (total === 0) return false;
  return countOwnedMonuments(tiles, owner) === total;
}

export function countPlayerSupplyTrucks(units: readonly Unit[], owner: PlayerId): number {
  return units.filter((unit) => unit.owner === owner && unit.kind === "supplyTruck").length;
}

export function allSupplyTrucksOnDropZones(state: GameState): boolean {
  const trucks = state.units.filter((unit) => unit.owner === "p1" && unit.kind === "supplyTruck");
  if (trucks.length === 0) return false;
  return trucks.every((truck) => getTile(state, truck.pos).kind === "dropZone");
}

export type ObjectiveProgress = {
  kind: VictoryCondition;
  label: string;
  p1Count: number;
  p2Count: number;
  target: number | null;
};

export function objectiveDescription(
  source: Pick<GameState, "victoryConditions" | "captureCityTarget" | "initialSupplyTruckCount">,
): string {
  const kind = activeVictoryCondition(source);
  if (kind === "capture") {
    return `Capture ${source.captureCityTarget ?? 0} cities.`;
  }
  if (kind === "monuments") {
    return "Capture all monuments.";
  }
  if (kind === "escort") {
    const truckCount = source.initialSupplyTruckCount ?? 0;
    if (truckCount > 1) {
      return "Escort all Supply Trucks to a drop zone.";
    }
    return "Escort the Supply Truck to a drop zone.";
  }
  return "Destroy all enemy units.";
}

export function objectiveProgress(state: GameState): ObjectiveProgress {
  const kind = activeVictoryCondition(state);
  const label = `Objective: ${objectiveDescription(state).replace(/\.$/, "")}`;
  if (kind === "capture") {
    const target = state.captureCityTarget ?? 0;
    return {
      kind,
      label,
      p1Count: countOwnedCities(state.tiles, "p1"),
      p2Count: countOwnedCities(state.tiles, "p2"),
      target,
    };
  }
  if (kind === "monuments") {
    return {
      kind,
      label,
      p1Count: countOwnedMonuments(state.tiles, "p1"),
      p2Count: countOwnedMonuments(state.tiles, "p2"),
      target: countMonuments(state.tiles),
    };
  }
  return {
    kind,
    label,
    p1Count: 0,
    p2Count: 0,
    target: null,
  };
}
